// deps
import React, { memo, ReactElement, useCallback, useMemo, useState } from "react";

// internal-deps
import { captureError, isEmpty } from "@swiggy-private/core-ui";
import { InputCardDetails, ProceedNewCardDetails } from "@swiggy-private/payment-types";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import InfoIcon from "@payment-ui/icons/infoIcon";

// components
import AddNewCardDetails from "./components/Details";
import AddNewCardSaveDetails from "./components/SaveDetails";
import { SecureCardInfo } from "@payment-ui/components/secure-card-info";
import OfferBanner from "./components/OfferBanner";

// interfaces
import { ADD_NEW_CARD_PROCEED_ACTION_STATUS, AddNewCardCommonProps } from "@payment-ui/interfaces";

// helpers
import { getCardValidator, getMonthAndYearFromExpiry, isValidCard, getCardIcon } from "@payment-ui/helpers/Card";
import { SimpleCardValidator } from "../../types/Juspay";
import { capitalizeFirstLetter } from "@payment-ui/helpers/String";

// test-ids
import { ADD_NEW_CARD_TEST_IDS } from "@swiggy-private/payment-test-ids";

// constants
import { DEFAULT_ADD_NEW_CARD_ERROR_STATE } from "@payment-ui/constants/Card";
import { useCardHalfCard } from "@payment-ui/hooks/useCardHalfCardActions";

import styles from "./AddNewCard.v4.scss";

type PaymentModesAddNewCardProps = AddNewCardCommonProps & {
    onProceed: (details: ProceedNewCardDetails) => void;
    addCardDetails?: ProceedNewCardDetails;
    isOfferEnabled: string;
    imageId: string;
};

const PROCEED_BTN_CTA = "Proceed";

export const PaymentModesAddNewCard = memo((props: PaymentModesAddNewCardProps): ReactElement => {
    const {
        onProceed,
        juspayTokenizationMeta,
        addCardDetails,
        onCardNumberChange,
        onSecureCardInfoView,
        onSecureCardInfoClickAction,
        cardBrandDetails,
        onKnowMoreTokenizationClickAction,
        skipClientCardValidation,
        onProceedToPayClick,
        isOfferEnabled,
        imageId,
        saveCardCtaHelperText,
    } = props;

    const [details, setDetails] = useState<InputCardDetails>({
        cardNumber: addCardDetails?.cardNumber || "",
        cvv: addCardDetails?.cvv || "",
        name: addCardDetails?.name || "",
        expiry: addCardDetails?.expiry || "",
        nickName: addCardDetails?.nickName || "",
    });

    const { showSecureCardInfo, onSecureCardInfoClick, closeSecureCardInfo } = useCardHalfCard({
        onSecureCardInfoClickAction,
        onKnowMoreTokenizationClickAction,
    });

    const [errors, setErrors] = useState(DEFAULT_ADD_NEW_CARD_ERROR_STATE);

    const isValidCardDetails = useCallback(
        (validator: SimpleCardValidator): boolean => {
            const errors = isValidCard(validator, details);
            setErrors(errors);
            return !errors.cardNumber && !errors.cvv && !errors.expiry && !errors.name;
        },
        [details],
    );

    const proceedToPay = useCallback(
        (saveCard: boolean, validator: SimpleCardValidator): void => {
            const cardDetails = validator.getCardDetails();
            const { month, year } = getMonthAndYearFromExpiry(details.expiry);
            const brand = cardDetails.card_type.toUpperCase();
            const cardIcon = getCardIcon(cardBrandDetails.cardBrand);
            const nickName = details.nickName.trim();

            onProceed({
                ...details,
                month,
                year,
                brand,
                saveCard,
                tokenizeSupport: cardBrandDetails.tokenizeSupport || false,
                cardBin: cardBrandDetails.cardBin ?? "",
                iconUrl: cardBrandDetails.url ?? "",
                cardIcon,
                nickName,
            });
        },
        [
            cardBrandDetails.cardBin,
            cardBrandDetails.cardBrand,
            cardBrandDetails.tokenizeSupport,
            cardBrandDetails.url,
            details,
            onProceed,
        ],
    );

    const onProceedClick = useCallback(
        (e: React.MouseEvent): void => {
            e.preventDefault();
            // adding try catch to handle and capture simple card validator module bugs
            try {
                const validator = getCardValidator(details.cardNumber.trim());

                if (skipClientCardValidation || isValidCardDetails(validator)) {
                    onProceedToPayClick(ADD_NEW_CARD_PROCEED_ACTION_STATUS.PROCEED_TO_PAY);
                    proceedToPay(true, validator);
                    return;
                }

                onProceedToPayClick(ADD_NEW_CARD_PROCEED_ACTION_STATUS.INVALID_CARD_DETAILS);
            } catch (e) {
                onProceedToPayClick(ADD_NEW_CARD_PROCEED_ACTION_STATUS.FAILED_TO_PROCEED);
                captureError(e);
            }
        },
        [details.cardNumber, isValidCardDetails, proceedToPay, skipClientCardValidation, onProceedToPayClick],
    );

    const resetError = useCallback((): void => setErrors(DEFAULT_ADD_NEW_CARD_ERROR_STATE), []);

    const updateDetails = useCallback((details: InputCardDetails): void => {
        setDetails(details);
    }, []);

    const areMandatoryFieldsFilled = useMemo(() => {
        const { nickName, ...otherDetails } = details;
        return Object.values(otherDetails).every((field) => field !== "");
    }, [details]);

    const detailsProps = useMemo(
        () => ({
            details,
            errors,
            resetError,
            updateDetails,
            onCardNumberChange,
            cardBrandDetails,
        }),
        [cardBrandDetails, details, errors, onCardNumberChange, resetError, updateDetails],
    );

    return (
        <>
            <OfferBanner isOfferEnabled={isOfferEnabled} imageId={imageId} />
            <div className={styles.newCard}>
                <div>
                    <AddNewCardDetails {...detailsProps} />
                    <div className={styles.newCardSelectionCardWrapper}>
                        {cardBrandDetails.tokenizeSupport != null && (
                            <AddNewCardSaveDetails
                                onInfoClick={onSecureCardInfoClick}
                                cardBrand={capitalizeFirstLetter(cardBrandDetails.cardBrand || "")}
                                tokenizeSupport={cardBrandDetails.tokenizeSupport}
                            />
                        )}
                    </div>
                </div>
                <div className={styles.newCardSaveContainer} data-addnewcard-proceed>
                    {!isEmpty(saveCardCtaHelperText) && (
                        <div className={styles.newCardSaveCtaHelper}>
                            <InfoIcon className={styles.newCardSaveCtaHelperIcon} size={16} />
                            <Text variant="V2.body3.neutral.regular" color="text_Med_Emphasis">
                                {saveCardCtaHelperText}
                            </Text>
                        </div>
                    )}
                    <SuperButton
                        buttonType="primary"
                        className={styles.newCardSave}
                        onClick={onProceedClick}
                        data-testid={ADD_NEW_CARD_TEST_IDS.PROCEED_TO_PAYMENT_BUTTON}
                        isDisabled={!areMandatoryFieldsFilled}
                        variant="fullwidth"
                        buttonSize="large"
                    >
                        {PROCEED_BTN_CTA}
                    </SuperButton>
                </div>
                {showSecureCardInfo.show && juspayTokenizationMeta && (
                    <SecureCardInfo
                        data={juspayTokenizationMeta}
                        {...showSecureCardInfo.actions}
                        onClose={closeSecureCardInfo}
                        cardBrand={capitalizeFirstLetter(cardBrandDetails.cardBrand || "")}
                        onLoad={onSecureCardInfoView}
                    />
                )}
            </div>
        </>
    );
});
